/**
 * ProductionSettingsPage — настройки для production-сабдомена.
 *
 * Пока единственная функция: выдача права удалять сделки
 * (can_delete_production) per-user. Доступна superuser/owner/admin.
 *
 * Бэк: GET /api/admin/users/  и  PATCH /api/admin/users/{id}/perm
 */
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth, hasAccess } from "@/auth/AuthContext";
import { Spinner } from "@/components/Spinner";
import { api } from "@/api/client";


interface AdminUser {
  id: string;
  email: string;
  display_name: string | null;
  role: string;
  is_superuser: boolean;
  is_active: boolean;
  departments: string[];
  can_delete_production: boolean;
}


async function fetchAdminUsers(): Promise<AdminUser[]> {
  return api(`/api/admin/users/`);
}

async function patchUserPerm(userId: string, payload: { can_delete_production?: boolean }): Promise<AdminUser> {
  return api(`/api/admin/users/${userId}/perm`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}


export function ProductionSettingsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [filter, setFilter] = useState("");
  const canEdit = hasAccess(user, "production-admin");

  const q = useQuery({
    queryKey: ["users-admin-list"],
    queryFn: fetchAdminUsers,
    staleTime: 30_000,
    enabled: canEdit,
  });

  const patchMut = useMutation({
    mutationFn: (vars: { id: string; can_delete_production: boolean }) =>
      patchUserPerm(vars.id, { can_delete_production: vars.can_delete_production }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ["users-admin-list"] });
      const prev = qc.getQueryData<AdminUser[]>(["users-admin-list"]);
      qc.setQueryData<AdminUser[]>(["users-admin-list"], (old) =>
        (old ?? []).map(u => u.id === vars.id ? { ...u, can_delete_production: vars.can_delete_production } : u)
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["users-admin-list"], ctx.prev);
      alert("Не удалось обновить право.");
    },
  });

  if (!canEdit) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--mute)" }}>
        Эта страница доступна только администраторам производства.
      </div>
    );
  }

  const users = q.data ?? [];
  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    let list = users.filter(u => u.is_active);
    if (f) {
      list = list.filter(u =>
        (u.display_name ?? "").toLowerCase().includes(f) ||
        u.email.toLowerCase().includes(f)
      );
    }
    list.sort((a, b) => (a.display_name ?? a.email).localeCompare(b.display_name ?? b.email, "ru"));
    return list;
  }, [users, filter]);

  return (
    <div style={{ padding: 24, maxWidth: 880, margin: "0 auto" }}>
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Настройки доступа</h1>
      <p style={{ marginTop: 6, color: "var(--mute)", fontSize: 13 }}>
        Кто может удалять сделки из карточек производства. По умолчанию могут только
        собственник и администраторы.
      </p>

      <div style={{ marginTop: 20, marginBottom: 12 }}>
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Поиск по имени или email…"
          style={{
            width: "100%", padding: "8px 12px", fontSize: 14,
            border: "1px solid var(--line)", borderRadius: 8,
            background: "#fafafa", outline: "none",
          }}
        />
      </div>

      {q.isLoading ? (
        <Spinner size="lg"/>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--mute)" }}>
          Никого не найдено.
        </div>
      ) : (
        <div style={{
          background: "#fff", border: "1px solid var(--line)",
          borderRadius: 12, overflow: "hidden",
        }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr auto auto",
            padding: "10px 16px", borderBottom: "1px solid var(--line)",
            background: "#fafafa",
            fontSize: 11, fontWeight: 600, textTransform: "uppercase",
            letterSpacing: 0.4, color: "var(--mute)",
            gap: 16,
          }}>
            <span>Сотрудник</span>
            <span>Роль</span>
            <span style={{ textAlign: "right" }}>Удалять сделки</span>
          </div>
          {filtered.map((u, i) => {
            const isPrivileged = u.is_superuser || u.role === "owner" || u.role === "admin";
            const effective = isPrivileged || u.can_delete_production;
            return (
              <div key={u.id} style={{
                display: "grid",
                gridTemplateColumns: "1fr auto auto",
                padding: "10px 16px",
                borderTop: i === 0 ? "none" : "1px solid var(--line)",
                gap: 16,
                alignItems: "center",
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: "var(--ink)", fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {u.display_name || u.email}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--mute)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {u.email}
                  </div>
                </div>
                <span style={{
                  padding: "2px 8px", borderRadius: 10,
                  background: roleBg(u.role, u.is_superuser),
                  color: "#fff", fontSize: 11, fontWeight: 600,
                  whiteSpace: "nowrap",
                }}>{roleLabel(u.role, u.is_superuser)}</span>
                <label style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  cursor: isPrivileged ? "not-allowed" : "pointer",
                  opacity: isPrivileged ? 0.6 : 1,
                  fontSize: 13,
                }}
                title={isPrivileged ? "Право всегда есть у этой роли — отключить нельзя" : ""}>
                  <input
                    type="checkbox"
                    checked={effective}
                    disabled={isPrivileged || patchMut.isPending}
                    onChange={(e) => patchMut.mutate({ id: u.id, can_delete_production: e.target.checked })}
                    style={{ width: 18, height: 18, cursor: "inherit" }}
                  />
                  <span style={{ color: effective ? "var(--green, #4fae4e)" : "var(--mute)", fontWeight: 600 }}>
                    {effective ? "может" : "нет"}
                  </span>
                </label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function roleLabel(role: string, isSuper: boolean): string {
  if (isSuper) return "Собственник";
  return {
    owner: "Собственник", admin: "Администратор", head_op: "Рук. ОП",
    op_manager: "Менеджер", accountant: "Бухгалтер", assistant: "Ассистент",
    field_engineer: "Инженер", warehouse_keeper: "Кладовщик", brigade: "Бригадир",
    storekeeper: "Кладовщик", bot: "Бот",
  }[role] || role;
}

function roleBg(role: string, isSuper: boolean): string {
  if (isSuper || role === "owner") return "#4fae4e";
  if (role === "admin") return "#3390ec";
  if (role === "accountant") return "#9c7ce5";
  if (role === "head_op") return "#e99a12";
  if (role === "field_engineer" || role === "brigade") return "#00acc1";
  if (role === "bot") return "#9e9e9e";
  return "#707579";
}
